import gql from "graphql-tag";
import {useMutation} from "@apollo/react-hooks";
import {ComplianceEngineResultFragment, PaxNodeComplianceFieldsFragment} from "../../../Queries/Compliance";
import {
    ComplianceCheckInPackageFragmentFragment, ComplianceEngineResultFragmentFragment,
    PaxNodeComplianceFieldsFragmentFragment
} from "../../../__generated__/graphql/types";
import {useQuery} from "react-apollo";
import ApolloClient from "apollo-client";
import {getAdHocValidationMessage} from "./adhoc-validation";

const COMPLIANCE_PAXNODE_MUTATION = gql`
    mutation ComplianceChecker_RunPaxNodeComplianceEngineCheck($paxNodeID: ID!){
        RunPaxNodeComplianceEngineCheck(paxNodeID: $paxNodeID){
            ...PaxNodeComplianceFieldsFragment
        }
    }
    ${PaxNodeComplianceFieldsFragment}
`

const COMPLIANCE_ADHOC_CHECK_QUERY = gql`
    query ComplianceChecker_AdHocComplianceCheck($checkInPackage: ComplianceCheckInPackageInput!){
        RunAdHocComplianceEngineCheck(checkInPackage: $checkInPackage){
            ...ComplianceEngineResultFragment
        }
    }
    ${ComplianceEngineResultFragment}
`

export function useCheckPaxNodeCompliance(paxNodeID: string) {

    const [ invoke, { loading, error, data } ] = useMutation(COMPLIANCE_PAXNODE_MUTATION, {
        variables: {
            paxNodeID: paxNodeID
        },
        update: (cache, { data: mutationData }) => {
            if (!mutationData || !mutationData.RunPaxNodeComplianceEngineCheck) {
                return;
            }

            const paxNode = mutationData.RunPaxNodeComplianceEngineCheck;

            cache.writeData({
                id: 'PaxNode:' + paxNode._id,
                data: {
                    lastComplianceEngineResult: paxNode.lastComplianceEngineResult
                }
            })
        }
    });

    return {
        loading: loading,
        error: error,
        run: () => invoke(),
        result: data?.RunPaxNodeComplianceEngineCheck as PaxNodeComplianceFieldsFragmentFragment
    }
}

export function useAdHocComplianceCheck(checkInPackage: ComplianceCheckInPackageFragmentFragment) {
    const validationMessage = getAdHocValidationMessage(checkInPackage);
    const { data, loading, error } = useQuery(COMPLIANCE_ADHOC_CHECK_QUERY, {
        variables: {
            checkInPackage: checkInPackage,
        },
        skip: !checkInPackage || !!validationMessage,
        fetchPolicy: 'cache-and-network'
    })

    return {
        loading: !validationMessage && loading,
        error: validationMessage ? undefined : error,
        validationMessage,
        result: validationMessage || !checkInPackage ? undefined : data?.RunAdHocComplianceEngineCheck as ComplianceEngineResultFragmentFragment
    }
}

export async function runAdHocComplianceCheck(client: ApolloClient<any>, checkInPackage: ComplianceCheckInPackageFragmentFragment) {
    const validationMessage = getAdHocValidationMessage(checkInPackage);
    if (validationMessage) throw new Error(validationMessage);
    const result = await client.query({
        query: COMPLIANCE_ADHOC_CHECK_QUERY,
        variables: {
            checkInPackage: checkInPackage,
        },
        fetchPolicy: 'network-only'
    });

    return result?.data?.RunAdHocComplianceEngineCheck as ComplianceEngineResultFragmentFragment;
}
